import { DragEvent, useEffect, useRef, useState } from "react";
import { supabase } from "./lib/supabase";
import "./vehicle-media.css";
import "./document-card-proportions.css";

type Media = {
  id: string;
  original_path: string;
  public_path: string | null;
  original_name: string;
  size_bytes: number;
  position: number;
  is_primary: boolean;
};
type ArchiveDocument = {
  id: string;
  category: string;
  title: string;
  original_name: string;
  storage_path: string;
  size_bytes: number;
  created_at: string;
  notes: string | null;
  document_scope?: "vehicle" | "financial" | "generated";
};
type MediaSettings = {
  max_vehicle_photos: number;
  max_photo_bytes: number;
  watermark_enabled: boolean;
  watermark_text: string;
  watermark_position: string;
  document_retention_years: number;
};
type ActionFeedback = {
  kind: "success" | "error" | "info";
  message: string;
};
export type PendingVehicleDocument = {
  id: string;
  category: string;
  title: string;
  notes: string;
  file: File;
  preparedAt: string;
};

const readableSize = (bytes: number) =>
  bytes < 1048576
    ? `${Math.ceil(bytes / 1024)} KB`
    : `${(bytes / 1048576).toFixed(1)} MB`;
const readableDate = (value: string) =>
  new Intl.DateTimeFormat("pt-PT", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
const safeName = (name: string) =>
  name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]/g, "-");
const fileHash = async (file: File) =>
  Array.from(
    new Uint8Array(
      await crypto.subtle.digest("SHA-256", await file.arrayBuffer()),
    ),
  )
    .map((x) => x.toString(16).padStart(2, "0"))
    .join("");

async function optimizedImage(file: File, settings: MediaSettings) {
  const image = await createImageBitmap(file),
    scale = Math.min(1, 1600 / image.width),
    canvas = document.createElement("canvas");
  canvas.width = Math.round(image.width * scale);
  canvas.height = Math.round(image.height * scale);
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Não foi possível preparar a versão pública.");
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  if (settings.watermark_enabled) {
    const padding = Math.max(18, canvas.width * 0.025);
    context.font = `700 ${Math.max(18, canvas.width * 0.025)}px Arial`;
    context.fillStyle = "rgba(255,255,255,.78)";
    context.shadowColor = "rgba(0,0,0,.55)";
    context.shadowBlur = 4;
    const width = context.measureText(settings.watermark_text).width,
      positions: Record<string, [number, number]> = {
        top_left: [padding, padding * 2],
        top_right: [canvas.width - width - padding, padding * 2],
        bottom_left: [padding, canvas.height - padding],
        bottom_right: [canvas.width - width - padding, canvas.height - padding],
        center: [(canvas.width - width) / 2, canvas.height / 2],
      };
    context.fillText(
      settings.watermark_text,
      ...positions[settings.watermark_position],
    );
  }
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", 0.82),
  );
  if (!blob) throw new Error("Não foi possível otimizar a fotografia.");
  return { blob, width: canvas.width, height: canvas.height };
}

export function VehicleMediaPanel({
  organizationId,
  vehicleId,
  writable,
  initialFiles = [],
  initialDocuments = [],
  onInitialFilesHandled,
  onInitialDocumentsHandled,
  mode = "all",
}: {
  organizationId: string;
  vehicleId: string;
  writable: boolean;
  initialFiles?: File[];
  initialDocuments?: PendingVehicleDocument[];
  onInitialFilesHandled?: () => void;
  onInitialDocumentsHandled?: () => void;
  mode?: "all" | "photos" | "documents";
}) {
  const [media, setMedia] = useState<Media[]>([]),
    [documents, setDocuments] = useState<ArchiveDocument[]>([]),
    [settings, setSettings] = useState<MediaSettings | null>(null),
    [urls, setUrls] = useState<Record<string, string>>({}),
    [notice, setNotice] = useState<string | null>(null),
    [uploading, setUploading] = useState<string[]>([]),
    [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 }),
    [failedFiles, setFailedFiles] = useState<File[]>([]),
    [failedInitialDocuments, setFailedInitialDocuments] = useState<
      PendingVehicleDocument[]
    >([]),
    [dragged, setDragged] = useState<string | null>(null),
    [docCategory, setDocCategory] = useState("Identificação"),
    [docTitle, setDocTitle] = useState(""),
    [docNotes, setDocNotes] = useState(""),
    [docFile, setDocFile] = useState<File | null>(null),
    [docSaving, setDocSaving] = useState(false),
    [documentTab, setDocumentTab] = useState<"vehicle" | "financial">("vehicle"),
    [documentFeedback, setDocumentFeedback] = useState<ActionFeedback | null>(
      null,
    ),
    [mediaAction, setMediaAction] = useState<string | null>(null),
    [mediaToRemove, setMediaToRemove] = useState<Media | null>(null),
    [removeReason, setRemoveReason] = useState(""),
    inputRef = useRef<HTMLInputElement>(null),
    cancelUpload = useRef(false),
    lastArchiveError = useRef<string | null>(null),
    initialFilesStarted = useRef(false),
    initialDocumentsStarted = useRef(false),
    draggedRef = useRef<string | null>(null);
  const load = async () => {
    const [m, d, s] = await Promise.all([
      supabase
        .from("vehicle_media")
        .select(
          "id,original_path,public_path,original_name,size_bytes,position,is_primary",
        )
        .eq("vehicle_id", vehicleId)
        .neq("status", "removed")
        .order("position"),
      supabase
        .from("archive_documents")
        .select(
          "id,category,title,original_name,storage_path,size_bytes,created_at,notes,document_scope",
        )
        .eq("vehicle_id", vehicleId)
        .is("archived_at", null)
        .order("created_at", { ascending: false }),
      supabase
        .from("organization_media_settings")
        .select(
          "max_vehicle_photos,max_photo_bytes,watermark_enabled,watermark_text,watermark_position,document_retention_years",
        )
        .eq("organization_id", organizationId)
        .single(),
    ]);
    const linked = await supabase
      .from("archive_document_links")
      .select("document_id")
      .eq("organization_id", organizationId)
      .eq("entity_type", "vehicle")
      .eq("entity_id", vehicleId);
    const linkedIds = (linked.data ?? []).map((item) => item.document_id);
    const linkedDocuments = linkedIds.length
      ? await supabase
          .from("archive_documents")
          .select(
            "id,category,title,original_name,storage_path,size_bytes,created_at,notes,document_scope",
          )
          .in("id", linkedIds)
          .neq("status", "archived")
      : { data: [], error: null };
    const combined = new Map<string, ArchiveDocument>();
    for (const item of [
      ...((d.data ?? []) as ArchiveDocument[]),
      ...((linkedDocuments.data ?? []) as ArchiveDocument[]),
    ])
      combined.set(item.id, item);
    setMedia((m.data ?? []) as Media[]);
    setDocuments(
      [...combined.values()].sort((a, b) =>
        b.created_at.localeCompare(a.created_at),
      ),
    );
    setSettings(s.data as MediaSettings | null);
    if (m.error || d.error || s.error || linked.error || linkedDocuments.error)
      setNotice(
        m.error?.message ??
          d.error?.message ??
          s.error?.message ??
          linked.error?.message ??
          linkedDocuments.error?.message ??
          null,
      );
    const next: Record<string, string> = {};
    for (const item of (m.data ?? []) as Media[]) {
      if (item.public_path)
        next[item.id] = supabase.storage
          .from("vehicle-media-public")
          .getPublicUrl(item.public_path).data.publicUrl;
      else {
        const signed = await supabase.storage
          .from("vehicle-media-originals")
          .createSignedUrl(item.original_path, 600, {
            transform: {
              width: 520,
              height: 360,
              resize: "cover",
              quality: 75,
            },
          });
        if (signed.data) next[item.id] = signed.data.signedUrl;
      }
    }
    setUrls(next);
  };
  useEffect(() => {
    void load();
  }, [vehicleId]);
  useEffect(() => {
    if (!documentFeedback || documentFeedback.kind === "error") return;
    const timeout = window.setTimeout(() => setDocumentFeedback(null), 10000);
    return () => window.clearTimeout(timeout);
  }, [documentFeedback]);
  useEffect(() => {
    if (!notice || uploading.length || mediaAction) return;
    const timeout = window.setTimeout(() => setNotice(null), 10000);
    return () => window.clearTimeout(timeout);
  }, [notice, uploading.length, mediaAction]);
  const remaining = Math.max(
    0,
    (settings?.max_vehicle_photos ?? 8) - media.length,
  );
  const uploadFiles = async (files: File[]) => {
    if (!settings || !writable) return;
    const compatible = files.filter((f) =>
        ["image/jpeg", "image/png", "image/webp"].includes(f.type),
      ),
      rejectedTypeCount = files.length - compatible.length,
      accepted = compatible.slice(0, remaining),
      rejectedQuotaCount = compatible.length - accepted.length;
    if (!accepted.length)
      return setNotice(
        remaining
          ? "Selecione fotografias JPG, PNG ou WebP."
          : "A viatura atingiu o limite de fotografias.",
      );
    cancelUpload.current = false;
    setFailedFiles([]);
    setUploading(accepted.map((f) => f.name));
    setUploadProgress({ done: 0, total: accepted.length });
    const failed: File[] = [];
    for (const file of accepted) {
      if (cancelUpload.current) break;
      let uploadedOriginalPath: string | null = null,
        uploadedPublicPath: string | null = null;
      try {
        if (file.size > settings.max_photo_bytes)
          throw new Error(
            `${file.name} excede ${readableSize(settings.max_photo_bytes)}.`,
          );
        const token = crypto.randomUUID(),
          name = safeName(file.name),
          originalPath = `${organizationId}/${vehicleId}/${token}-${name}`,
          publicPath = `${organizationId}/${vehicleId}/${token}.jpg`;
        const original = await supabase.storage
          .from("vehicle-media-originals")
          .upload(originalPath, file, { cacheControl: "31536000" });
        if (original.error) throw original.error;
        uploadedOriginalPath = originalPath;
        const optimized = await optimizedImage(file, settings),
          published = await supabase.storage
            .from("vehicle-media-public")
            .upload(publicPath, optimized.blob, {
              contentType: "image/jpeg",
              cacheControl: "31536000",
            });
        if (published.error) throw published.error;
        uploadedPublicPath = publicPath;
        const registered = await supabase.rpc("register_vehicle_media", {
          p_vehicle_id: vehicleId,
          p_original_path: originalPath,
          p_public_path: publicPath,
          p_original_name: file.name,
          p_mime_type: file.type,
          p_size_bytes: file.size,
          p_width: optimized.width,
          p_height: optimized.height,
          p_watermark_applied: settings.watermark_enabled,
        });
        if (registered.error) throw registered.error;
        uploadedOriginalPath = null;
        uploadedPublicPath = null;
      } catch (error) {
        await Promise.all([
          uploadedOriginalPath
            ? supabase.storage
                .from("vehicle-media-originals")
                .remove([uploadedOriginalPath])
            : Promise.resolve(),
          uploadedPublicPath
            ? supabase.storage
                .from("vehicle-media-public")
                .remove([uploadedPublicPath])
            : Promise.resolve(),
        ]);
        failed.push(file);
        setNotice(
          error instanceof Error ? error.message : "Falha no carregamento.",
        );
      } finally {
        setUploadProgress((value) => ({ ...value, done: value.done + 1 }));
      }
    }
    setFailedFiles(failed);
    setUploading([]);
    if (cancelUpload.current)
      setNotice(
        "Carregamento cancelado. As fotografias já concluídas foram preservadas.",
      );
    else if (!failed.length)
      setNotice(
        `${accepted.length === 1 ? "Fotografia guardada" : `${accepted.length} fotografias guardadas`}.${rejectedQuotaCount ? ` ${rejectedQuotaCount} não foram adicionadas porque o limite foi atingido.` : ""}${rejectedTypeCount ? ` ${rejectedTypeCount} ficheiro(s) incompatível(eis) foram ignorados.` : ""}`,
      );
    await load();
  };
  useEffect(() => {
    if (
      !settings ||
      !writable ||
      !initialFiles.length ||
      initialFilesStarted.current
    )
      return;
    initialFilesStarted.current = true;
    void uploadFiles(initialFiles).finally(() => onInitialFilesHandled?.());
  }, [settings, vehicleId]);
  const dropZone = (event: DragEvent) => {
    event.preventDefault();
    void uploadFiles(Array.from(event.dataTransfer.files));
  };
  const reorder = async (target: string) => {
    const source = draggedRef.current;
    if (!source || source === target) return;
    const next = [...media],
      from = next.findIndex((x) => x.id === source),
      to = next.findIndex((x) => x.id === target);
    if (from < 0 || to < 0) return;
    next.splice(to, 0, next.splice(from, 1)[0]);
    setMedia(next);
    setMediaAction("reorder");
    setNotice("A guardar a ordem das fotografias…");
    const result = await supabase.rpc("reorder_vehicle_media", {
      p_vehicle_id: vehicleId,
      p_media_ids: next.map((x) => x.id),
    });
    if (result.error) {
      setNotice(result.error.message);
      await load();
    } else setNotice("Ordem das fotografias guardada.");
    setDragged(null);
    draggedRef.current = null;
    setMediaAction(null);
  };
  const primary = async (id: string) => {
    setMediaAction(id);
    setNotice("A atualizar a fotografia principal…");
    const { error } = await supabase.rpc("set_primary_vehicle_media", {
      p_media_id: id,
    });
    setNotice(error?.message ?? "Fotografia principal atualizada.");
    if (!error) await load();
    setMediaAction(null);
  };
  const remove = async () => {
    const item = mediaToRemove,
      reason = removeReason.trim();
    if (!item || reason.length < 3) return;
    setMediaAction(item.id);
    setNotice("A remover a fotografia…");
    const { error } = await supabase.rpc("remove_vehicle_media", {
      p_media_id: item.id,
      p_reason: reason,
    });
    if (!error) {
      await Promise.all([
        item.public_path
          ? supabase.storage
              .from("vehicle-media-public")
              .remove([item.public_path])
          : Promise.resolve(),
      ]);
    }
    setNotice(
      error?.message ?? "Fotografia removida; a ocorrência ficou no histórico.",
    );
    if (!error) {
      setMediaToRemove(null);
      setRemoveReason("");
      await load();
    }
    setMediaAction(null);
  };
  const archiveFile = async (item: PendingVehicleDocument) => {
    lastArchiveError.current = null;
    try {
      const file = item.file,
        hash = await fileHash(file),
        path = `${organizationId}/vehicle/${vehicleId}/${crypto.randomUUID()}-${safeName(file.name)}`,
        upload = await supabase.storage
          .from("stand-document-archive")
          .upload(path, file);
      if (upload.error) throw upload.error;
      const saved = await supabase.rpc("register_archive_document", {
        p_organization_id: organizationId,
        p_vehicle_id: vehicleId,
        p_entity_type: "vehicle",
        p_entity_id: vehicleId,
        p_category: item.category,
        p_title: item.title,
        p_storage_path: path,
        p_original_name: file.name,
        p_mime_type: file.type || "application/octet-stream",
        p_size_bytes: file.size,
        p_sha256: hash,
        p_notes: item.notes,
      });
      if (saved.error) {
        await supabase.storage.from("stand-document-archive").remove([path]);
        throw saved.error;
      }
      return true;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Não foi possível arquivar o documento.";
      lastArchiveError.current = message;
      return false;
    }
  };
  const archive = async () => {
    const file = docFile;
    if (!file?.size) {
      setDocumentFeedback({
        kind: "error",
        message: "Não foi possível guardar: selecione primeiro um ficheiro.",
      });
      return;
    }
    setDocSaving(true);
    setNotice(null);
    setDocumentFeedback({ kind: "info", message: "A guardar o documento…" });
    try {
      const ok = await archiveFile({
        id: crypto.randomUUID(),
        category: docCategory,
        title: docTitle.trim() || file.name,
        notes: docNotes.trim(),
        file,
        preparedAt: new Date().toISOString(),
      });
      if (!ok) {
        setDocumentFeedback({
          kind: "error",
          message: `Não foi possível guardar: ${lastArchiveError.current ?? "ocorreu um erro inesperado."}`,
        });
        return;
      }
      setDocTitle("");
      setDocNotes("");
      setDocFile(null);
      setDocumentFeedback({
        kind: "success",
        message: "Documento guardado no perfil da viatura.",
      });
      await load();
    } catch (error) {
      setDocumentFeedback({
        kind: "error",
        message:
          error instanceof Error
            ? `Não foi possível guardar: ${error.message}`
            : "Não foi possível guardar o documento.",
      });
    } finally {
      setDocSaving(false);
    }
  };
  const archiveInitialDocuments = async (items: PendingVehicleDocument[]) => {
    const failed: PendingVehicleDocument[] = [];
    for (const item of items) if (!(await archiveFile(item))) failed.push(item);
    setFailedInitialDocuments(failed);
    if (!failed.length) {
      onInitialDocumentsHandled?.();
      setNotice(`${items.length} documento(s) arquivado(s).`);
    } else
      setNotice(
        `${failed.length} documento(s) não foram arquivados: ${lastArchiveError.current ?? "ocorreu um erro inesperado."} Pode repetir o envio.`,
      );
    await load();
  };
  useEffect(() => {
    if (
      !writable ||
      !initialDocuments.length ||
      initialDocumentsStarted.current
    )
      return;
    initialDocumentsStarted.current = true;
    void archiveInitialDocuments(initialDocuments);
  }, [vehicleId, writable]);
  const openDocument = async (item: ArchiveDocument) => {
    const { data, error } = await supabase.functions.invoke(
      "open-archive-document",
      {
        body: {
          document_id: item.id,
          purpose: "Consulta no perfil da viatura",
        },
      },
    );
    if (error || !data?.signed_url)
      return setNotice(
        data?.error ?? error?.message ?? "Não foi possível abrir o documento.",
      );
    window.open(data.signed_url, "_blank", "noopener,noreferrer");
    setNotice(
      "Documento aberto. A ligação expira em 60 segundos e o acesso ficou registado.",
    );
  };
  return (
    <fieldset className="stock-section vehicle-media-section">
      <legend>{mode === "photos" ? "Fotografias" : mode === "documents" ? "Documentos" : "Fotografias e documentos"}</legend>
      {mode !== "documents" && <>
      <p className="stock-section-help">
        Arraste várias fotografias, reorganize-as livremente e escolha a
        principal. O original fica privado; a versão pública é otimizada e pode
        receber a marca de água configurada.
      </p>
      {notice && (
        <button
          type="button"
          className="stock-notice"
          onClick={() => setNotice(null)}
        >
          {notice} ×
        </button>
      )}
      <div className="media-usage">
        <strong>
          {media.length} de {settings?.max_vehicle_photos ?? 8} fotografias
        </strong>
        <span>
          {remaining} lugares disponíveis · original máximo{" "}
          {readableSize(settings?.max_photo_bytes ?? 15728640)}
        </span>
      </div>
      <div
        className="media-dropzone"
        onDragOver={(e) => e.preventDefault()}
        onDrop={dropZone}
        onClick={() =>
          !uploading.length && writable && inputRef.current?.click()
        }
      >
        <input
          ref={inputRef}
          hidden
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          onChange={(e) => void uploadFiles(Array.from(e.target.files ?? []))}
        />
        <strong>
          {uploading.length
            ? `A carregar ${uploadProgress.done} de ${uploadProgress.total}…`
            : "Arraste fotografias para aqui"}
        </strong>
        <span>ou clique para selecionar várias de uma vez</span>
        {uploading.length > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              cancelUpload.current = true;
            }}
          >
            Cancelar restantes
          </button>
        )}
        {!uploading.length && failedFiles.length > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              void uploadFiles(failedFiles);
            }}
          >
            Repetir {failedFiles.length} falha(s)
          </button>
        )}
      </div>
      <div className="media-grid">
        {media.map((item) => (
          <article
            key={item.id}
            draggable={writable && mediaAction === null}
            onDragStart={(event) => {
              setDragged(item.id);
              draggedRef.current = item.id;
              event.dataTransfer.effectAllowed = "move";
              event.dataTransfer.setData("text/plain", item.id);
            }}
            onDragEnd={() => {
              setDragged(null);
              draggedRef.current = null;
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              void reorder(item.id);
            }}
          >
            <img src={urls[item.id]} alt={item.original_name} />
            {item.is_primary && <b>Principal</b>}
            <div>
              <strong>{item.original_name}</strong>
              <span>{readableSize(item.size_bytes)}</span>
            </div>
            {writable && (
              <footer>
                <button
                  type="button"
                  onClick={() => void primary(item.id)}
                  disabled={item.is_primary || mediaAction !== null}
                >
                  Tornar principal
                </button>
                <button
                  type="button"
                  disabled={mediaAction !== null}
                  onClick={() => setMediaToRemove(item)}
                >
                  {mediaAction === item.id ? "A processar…" : "Remover"}
                </button>
              </footer>
            )}
          </article>
        ))}
      </div>
      {mediaToRemove && (
        <div className="media-removal-backdrop" role="presentation">
          <form
            className="media-removal-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="media-removal-title"
            onSubmit={(event) => {
              event.preventDefault();
              void remove();
            }}
          >
            <h3 id="media-removal-title">Remover fotografia</h3>
            <p>
              A fotografia deixa de aparecer, mas o original e o motivo ficam
              preservados no histórico.
            </p>
            <label>
              <span>Motivo da remoção</span>
              <textarea
                autoFocus
                required
                minLength={3}
                value={removeReason}
                onChange={(event) => setRemoveReason(event.target.value)}
              />
            </label>
            <footer>
              <button
                type="button"
                className="stock-secondary"
                disabled={mediaAction !== null}
                onClick={() => {
                  setMediaToRemove(null);
                  setRemoveReason("");
                }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="stock-primary"
                disabled={
                  mediaAction !== null || removeReason.trim().length < 3
                }
              >
                {mediaAction ? "A remover…" : "Guardar"}
              </button>
            </footer>
          </form>
        </div>
      )}
      </>}
      {mode !== "photos" && (
      <div className="vehicle-documents">
        <h3>Arquivo documental da viatura</h3>
        <p className="stock-section-help">Separe os documentos próprios da viatura dos documentos que integram o seu circuito financeiro.</p>
        <nav className="vehicle-document-tabs" aria-label="Tipos de documentos da viatura">
          <button type="button" className={documentTab === "vehicle" ? "active" : ""} onClick={() => setDocumentTab("vehicle")}>Da viatura</button>
          <button type="button" className={documentTab === "financial" ? "active" : ""} onClick={() => setDocumentTab("financial")}>Financeiros</button>
        </nav>
        {failedInitialDocuments.length > 0 && (
          <button
            type="button"
            className="stock-secondary"
            onClick={() => void archiveInitialDocuments(failedInitialDocuments)}
          >
            Repetir documentos pendentes
          </button>
        )}
        {writable && (
          <div className="vehicle-document-editor">
            <div className="document-fields-row">
              <label>
                <span>Categoria</span>
                <select
                  value={docCategory}
                  onChange={(e) => setDocCategory(e.target.value)}
                >
                  {documentTab === "vehicle" ? <>
                    <option>Identificação</option><option>DUA</option><option>Inspeção</option><option>DAV</option><option>Legalização</option><option>Garantia</option><option>Outro</option>
                  </> : <>
                    <option>Fatura de compra</option><option>Fatura de manutenção</option><option>Aquisição</option><option>Manutenção</option><option>Outro financeiro</option>
                  </>}
                </select>
              </label>
              <label>
                <span className="field-label-row">
                  <span>Nome do ficheiro</span>
                  <em>Opcional</em>
                </span>
                <input
                  type="text"
                  autoComplete="off"
                  data-1p-ignore
                  data-lpignore="true"
                  value={docTitle}
                  onChange={(e) => setDocTitle(e.target.value)}
                />
              </label>
            </div>
            <label className="document-notes-field">
              <span>Notas</span>
              <textarea
                value={docNotes}
                onChange={(e) => setDocNotes(e.target.value)}
                rows={3}
              />
            </label>
            <div className="document-actions-row">
              <label className="document-file-button">
                <input
                  key={
                    docFile
                      ? `${docFile.name}-${docFile.lastModified}`
                      : "empty"
                  }
                  type="file"
                  disabled={docSaving}
                  onChange={(e) => {
                    setDocFile(e.target.files?.[0] ?? null);
                    setDocumentFeedback(null);
                  }}
                />
                <span>{docFile?.name ?? "Escolher ficheiro"}</span>
              </label>
              <button
                type="button"
                className="stock-secondary"
                disabled={docSaving}
                onClick={() => void archive()}
              >
                {docSaving ? "A guardar…" : "Guardar"}
              </button>
            </div>
            {documentFeedback && (
              <div
                className={`document-feedback ${documentFeedback.kind}`}
                role={documentFeedback.kind === "error" ? "alert" : "status"}
                aria-live="polite"
              >
                <span>{documentFeedback.message}</span>
                <button
                  type="button"
                  aria-label="Fechar mensagem"
                  onClick={() => setDocumentFeedback(null)}
                >
                  ×
                </button>
              </div>
            )}
          </div>
        )}
        <div className="document-list">
          {documents.filter((item) => {
            if (item.document_scope === "generated") return false;
            const financial = item.document_scope === "financial" || ["Fatura", "Fatura de compra", "Fatura de manutenção", "Aquisição", "Manutenção", "Outro financeiro"].includes(item.category);
            return documentTab === "financial" ? financial : !financial;
          }).map((item) => (
            <button
              type="button"
              key={item.id}
              onClick={() => void openDocument(item)}
            >
              <span className="document-card-content">
                <strong>{item.title}</strong>
                <dl>
                  <div>
                    <dt>Categoria</dt>
                    <dd>{item.category}</dd>
                  </div>
                  <div>
                    <dt>Tamanho</dt>
                    <dd>{readableSize(item.size_bytes)}</dd>
                  </div>
                  <div>
                    <dt>Data</dt>
                    <dd>{readableDate(item.created_at)}</dd>
                  </div>
                  <div>
                    <dt>Ficheiro original</dt>
                    <dd>{item.original_name}</dd>
                  </div>
                  <div className="document-card-notes">
                    <dt>Notas</dt>
                    <dd>{item.notes || "Sem notas"}</dd>
                  </div>
                </dl>
              </span>
            </button>
          ))}
        </div>
      </div>
      )}
    </fieldset>
  );
}
